/**
 * Latin Squares generator (EXAM.md §5, PLAN.md §2b).
 *
 * 5×5, letters A–E, one `?` cell, five options always shown. Only the `?`
 * must be uniquely determined — the rest of the grid need not be completable.
 *
 * Difficulty is MEASURED deduction depth: how many forced naked-single
 * placements (row/column single-candidate placement — the technique the
 * official solutions use) it takes until the `?` is forced. Bands (see
 * PLAN-Z §3 for the mechanical recalibration): low = 0–4, medium = 5–6,
 * high = 7–12. Puzzles needing logic beyond naked singles are rejected —
 * the candidate has no paper.
 *
 * Construction: complete square (cyclic base, random row/column/letter
 * permutations), all cells hidden, then clues re-added one at a time,
 * cheapest-first (each step commits the clue that minimises the measured
 * depth), until the `?` resolves with a depth inside the band.
 *
 * Soundness: a naked single (a cell whose row/column intersection leaves
 * exactly one letter) is forced in EVERY completion, so naked-singles
 * resolution of the `?` implies the `?` is uniquely determined. The test
 * suite verifies this independently with a completion checker.
 *
 * This module must stay DOM-free and dependency-free (PLAN-Z convention 4).
 */
import { mulberry32, pick, randomInt, shuffle } from './prng.ts'
import type { Difficulty, Question, Step } from './types.ts'

export const LATIN_LETTERS = ['A', 'B', 'C', 'D', 'E'] as const
/** Official solution-key column names (EXAM.md §5). */
export const COL_NAMES = ['α', 'β', 'γ', 'δ', 'ε'] as const
const N = 5

export interface LatinPrompt {
  /** Visible clues; null = empty cell. */
  cells: (string | null)[][]
  question: { row: number; col: number }
}

export interface LatinAnswer {
  letter: string
}

export interface Placement {
  row: number
  col: number
  letter: string
  reason: string
}

export interface SolveResult {
  resolved: boolean
  /**
   * Deduction depth: the number of forced naked-single placements before the
   * `?` resolves (the fixed point is order-independent). The `?`'s own
   * placement is the final deduction step on top of this.
   */
  depth: number
  /** Forced placements before the `?`, in propagation order. */
  placements: Placement[]
  answer: string | null
}

type Grid = (string | null)[][]

function missingInRow(grid: Grid, r: number): string[] {
  return LATIN_LETTERS.filter((L) => !grid[r].includes(L))
}

function missingInCol(grid: Grid, c: number): string[] {
  return LATIN_LETTERS.filter((L) => !grid.some((row) => row[c] === L))
}

function candidatesFor(grid: Grid, r: number, c: number): string[] {
  const rowMissing = missingInRow(grid, r)
  const colMissing = missingInCol(grid, c)
  return rowMissing.filter((L) => colMissing.includes(L))
}

function fmtList(letters: string[]): string {
  if (letters.length <= 1) return letters.join('')
  return `${letters.slice(0, -1).join(', ')} and ${letters[letters.length - 1]}`
}

function reasonFor(grid: Grid, r: number, c: number, letter: string): string {
  const rowMissing = missingInRow(grid, r)
  const colMissing = missingInCol(grid, c)
  if (rowMissing.length === 1) return `row ${r + 1} is missing only ${letter}`
  if (colMissing.length === 1) return `column ${COL_NAMES[c]} is missing only ${letter}`
  return (
    `row ${r + 1} is missing ${fmtList(rowMissing)} and column ${COL_NAMES[c]} is missing ${fmtList(colMissing)}; ` +
    `${letter} is the only letter that fits both`
  )
}

/**
 * Solve with naked singles only. Each round places every currently forced
 * cell simultaneously (sound for grids derived from a valid square; a
 * conflict marks the grid invalid). Returns as soon as the `?` is forced.
 */
export function solveNakedSingles(cells: Grid, question: { row: number; col: number }): SolveResult {
  const grid: Grid = cells.map((row) => row.slice())
  const placements: Placement[] = []
  for (let round = 1; round <= 60; round++) {
    const qCands = candidatesFor(grid, question.row, question.col)
    if (qCands.length === 1) {
      return { resolved: true, depth: placements.length, placements, answer: qCands[0] }
    }
    const wave: Placement[] = []
    for (let r = 0; r < N; r++) {
      for (let c = 0; c < N; c++) {
        if ((r === question.row && c === question.col) || grid[r][c] !== null) continue
        const cands = candidatesFor(grid, r, c)
        if (cands.length === 1) {
          wave.push({ row: r, col: c, letter: cands[0], reason: reasonFor(grid, r, c, cands[0]) })
        }
      }
    }
    if (wave.length === 0) {
      return { resolved: false, depth: placements.length, placements, answer: null }
    }
    // Consistency guard: two simultaneous placements must not collide.
    for (let i = 0; i < wave.length; i++) {
      for (let j = i + 1; j < wave.length; j++) {
        const a = wave[i]
        const b = wave[j]
        const sameUnit = a.row === b.row || a.col === b.col
        if (sameUnit && a.letter === b.letter) {
          return { resolved: false, depth: placements.length, placements, answer: null }
        }
      }
    }
    for (const p of wave) grid[p.row][p.col] = p.letter
    placements.push(...wave)
  }
  return { resolved: false, depth: placements.length, placements, answer: null }
}

/** A complete valid square: cyclic base (a·r + b·c mod 5) with random
 * row/column/letter permutations. */
function completeSquare(draw: () => number): string[][] {
  const units = [1, 2, 3, 4]
  const a = pick(draw, units)
  const b = pick(draw, units)
  const rowPerm = shuffle(draw, [0, 1, 2, 3, 4])
  const colPerm = shuffle(draw, [0, 1, 2, 3, 4])
  const letterPerm = shuffle(draw, [0, 1, 2, 3, 4])
  const square: string[][] = []
  for (let r = 0; r < N; r++) {
    const row: string[] = []
    for (let c = 0; c < N; c++) {
      row.push(LATIN_LETTERS[letterPerm[(a * rowPerm[r] + b * colPerm[c]) % N]])
    }
    square.push(row)
  }
  return square
}

/** Difficulty bands in measured depth. Recalibrated mechanically (see
 * PLAN-Z §3): the six official exercises measure 0, 6, 12, 11, 8 and 7
 * forced placements under this metric, so the bands are set to align with
 * the official low/medium/high labels where the metric allows. */
const DEPTH_BAND: Record<Difficulty, [number, number]> = {
  low: [0, 4],
  medium: [5, 8],
  high: [9, 12],
}

/**
 * Generate one Latin Squares item. Pure: same (difficulty, seed) → same question.
 */
export function generateLatin(
  difficulty: Difficulty,
  seed: number,
): Question<LatinPrompt, LatinAnswer> {
  const draw = mulberry32(seed)
  const [minDepth, maxDepth] = DEPTH_BAND[difficulty]
  for (let attempt = 0; attempt < 500; attempt++) {
    const square = completeSquare(draw)
    const question = { row: randomInt(draw, 0, N - 1), col: randomInt(draw, 0, N - 1) }
    const cells: Grid = Array.from({ length: N }, () => Array.from({ length: N }, () => null))

    // Re-add clues one at a time, cheapest-first, until `?` resolves with a
    // depth inside the band. Adding clues never increases the depth.
    let result = solveNakedSingles(cells, question)
    for (let step = 0; step < 40; step++) {
      if (result.resolved && result.depth >= minDepth && result.depth <= maxDepth) break
      let best: { r: number; c: number; score: number } | null = null
      for (const { r, c } of shuffle(draw, hiddenNonQuestion(cells, question))) {
        cells[r][c] = square[r][c]
        const trial = solveNakedSingles(cells, question)
        cells[r][c] = null
        const score = trial.resolved ? trial.depth : 1000 - trial.placements.length
        if (best === null || score < best.score) best = { r, c, score }
      }
      if (best === null) break
      cells[best.r][best.c] = square[best.r][best.c]
      result = solveNakedSingles(cells, question)
    }
    if (!result.resolved || result.depth < minDepth || result.depth > maxDepth) continue

    // Densify: sprinkle extra clues while the item stays inside the band, so
    // easier items look closer to the official presentation (more givens).
    const extra = randomInt(draw, 2, 6)
    for (let i = 0; i < extra; i++) {
      const hidden = shuffle(draw, hiddenNonQuestion(cells, question))
      if (hidden.length === 0) break
      const { r, c } = hidden[0]
      cells[r][c] = square[r][c]
      const trial = solveNakedSingles(cells, question)
      if (!trial.resolved || trial.depth < minDepth || trial.depth > maxDepth) {
        cells[r][c] = null
      }
    }
    result = solveNakedSingles(cells, question)
    if (!result.resolved || result.depth < minDepth || result.depth > maxDepth) continue

    const steps: Step[] = result.placements.map((p) => ({
      text: `${COL_NAMES[p.col]}${p.row + 1} = ${p.letter} — ${p.reason}.`,
    }))
    steps.push({
      text: `The ? (${COL_NAMES[question.col]}${question.row + 1}) must be ${result.answer}.`,
    })
    return {
      subtest: 'latin',
      difficulty,
      seed,
      prompt: { cells, question },
      answer: { letter: result.answer! },
      explanation: steps,
    }
  }
  throw new Error(`latin: failed to construct a valid item for seed ${seed} (${difficulty})`)
}

function hiddenNonQuestion(
  cells: Grid,
  question: { row: number; col: number },
): { r: number; c: number }[] {
  const out: { r: number; c: number }[] = []
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (cells[r][c] === null && !(r === question.row && c === question.col)) out.push({ r, c })
    }
  }
  return out
}
